import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getLockEmspUserDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/lock-emsp-user.form-default-values';
import { LockEmspUserByIdModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/lock-emsp-user.model';
import React, { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useEmspBoLockEmspUserMutation } from '@app/core/service/emsp-bo-emsp-user.service';

import {
  LockEmspBoEmspUserContextProvider,
  useEmspBoEmspUserContext,
} from '../emsp-bo-emsp-user.context';
import { EmspBoEmspUserLockCreateFormController } from './emsp-bo-emsp-user-lock-create-form/emsp-bo-emsp-user-lock-create-form.controller';

interface EmspBoEmspUserLockCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const EmspBoEmspUserLockCreateController = ({
  onCancel,
  onSuccess,
}: EmspBoEmspUserLockCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [lockEmspUserMutation] = useEmspBoLockEmspUserMutation();
  const emspUser = useEmspBoEmspUserContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<LockEmspUserByIdModel> = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.emsp-bo-emsp-user-lock-create.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<LockEmspUserByIdModel> = (
    fieldValues,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);

    lockEmspUserMutation(fieldValues)
      .unwrap()
      .then(() => {
        toast.success(t('$.emsp-bo-emsp-user-lock-create.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.emsp-bo-emsp-user-lock-create.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getLockEmspUserDefaultValues(emspUser.id);

  return (
    <LockEmspBoEmspUserContextProvider data={data}>
      <EmspBoEmspUserLockCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </LockEmspBoEmspUserContextProvider>
  );
};
